import { useMemo, useState } from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import "./tablaAgrupada.css";

const defaultRowId = (r) => r.IndentificadorId ?? r.IdentificadorId ?? r.id;
const safeKey = (s) => String(s ?? "—").replace(/[^\wáéíóúÁÉÍÓÚñÑ.-]+/g, "_");


function buildTree(data, groupBy, rowId, showLeaf) {
  const [g1, g2] = groupBy;
  const M = {};

  (data || []).forEach((r, idx) => {
    const a = g1.key(r);
    const aKey = `A-${safeKey(a)}`;
    if (!M[aKey]) {
      M[aKey] = {
        key: aKey,
        data: { tipo: g1.label, label: String(a ?? "—"), g1: a },
        childrenMap: {},
      };
    }
    const A = M[aKey];

    if (g2) {
      const b = g2.key(r);
      const bKey = `${aKey}__B-${safeKey(b)}`;
      if (!A.childrenMap[bKey]) {
        A.childrenMap[bKey] = {
          key: bKey,
          data: { tipo: g2.label, label: String(b ?? "—"), g1: a, g2: b },
          ...(showLeaf ? { children: [] } : null), 
        };
      }
      if (showLeaf) {
        A.childrenMap[bKey].children.push({
          key: `${bKey}__R-${safeKey(rowId(r) ?? idx)}`,
          data: { ...r, tipo: "Registro", g1: a, g2: b },
        });
      }
    } else {
      
      if (!A.children) A.children = [];
      if (showLeaf) {
        A.children.push({
          key: `${aKey}__R-${safeKey(rowId(r) ?? idx)}`,
          data: { ...r, tipo: "Registro", g1: a },
        });
      }
    }
  });

  return Object.values(M).map((A) => ({
    key: A.key,
    data: A.data,
    children: A.children ?? Object.values(A.childrenMap || {}),
  }));
}

export default function TablaAgrupadaReusable({
  data = [],
  groupBy = [
    { label: "Grupo", key: (r) => r.grupo },
    { label: "Subgrupo", key: (r) => r.subgrupo },
  ],
  rowId = defaultRowId,

  columns = [],
  onDelete,                 
  onPrint,                  
  scrollHeight = "420px",
  scrollDesktopOnly = false,
  selectionKeys,
  onSelectionKeysChange,
  headerName = "Nombre",
  showLeafRecords = false,
  
  leafText = (r) =>
    r.MercaderiaDescripcion ?? r.producto ?? r.name ?? r.descripcion ?? "",
}) {
  const nodes = useMemo(
    () => buildTree(data, groupBy, rowId, showLeafRecords),
    [data, groupBy, rowId, showLeafRecords]
  );

  const [internalSel, setInternalSel] = useState({});
  const selected = selectionKeys ?? internalSel;
  const setSelected = onSelectionKeysChange ?? setInternalSel;

  const isDesktop = typeof window !== "undefined" ? window.innerWidth >= 768 : true;
  const enableScroll = scrollDesktopOnly ? isDesktop : true;

  
  const leafRecords = (node) => {
    const t = node?.data?.tipo;
    if (t === "Registro") return [node.data];
    if (t === groupBy[0].label) {
      return (data || []).filter((r) => groupBy[0].key(r) === node.data.g1);
    }
    if (groupBy[1] && t === groupBy[1].label) {
      return (data || []).filter(
        (r) => groupBy[0].key(r) === node.data.g1 && groupBy[1].key(r) === node.data.g2
      );
    }
    return [];
  };

  
  const firstColumnBody = (node) =>
    node.data?.tipo === "Registro" ? leafText(node.data) : node.data?.label ?? "";

  const accionesTemplate = (node) => {
    
    const records = leafRecords(node);
    const ids = records.map(rowId).filter(Boolean);

    return (
      <div className="acciones">
        {onDelete && (
          <button
            type="button"
            className="p-button p-button-text"
            title="Eliminar"
            onClick={() => onDelete(node.data?.tipo, { node, records, ids })}
          >
            <i className="pi pi-trash" />
          </button>
        )}
        {onPrint && (
          <button
            type="button"
            className="p-button p-button-text"
            title="Imprimir"
            onClick={() => onPrint(node.data?.tipo, { node, records, ids })}
          >
            <i className="pi pi-print" />
          </button>
        )}
      </div>
    );
  };

  
  const makeBody = (col) => (node) => {
    if (node.data?.tipo === "Registro") {
      const val =
        typeof col.field === "function" ? col.field(node.data) : node.data[col.field];
      return col.format ? col.format(val) : val;
    }
    if (col.aggregate) {
      const vals = leafRecords(node).map((r) =>
        typeof col.field === "function" ? col.field(r) : r[col.field]
      );
      if (col.aggregate === "sum")
        return col.format
          ? col.format(vals.reduce((a, b) => a + (Number(b) || 0), 0))
          : vals.reduce((a, b) => a + (Number(b) || 0), 0);
      if (col.aggregate === "count") return vals.length;
    }
    return col.groupText ? col.groupText(node.data) : null;
  };

  return (
    <div className="tabla-agrupada">
      <TreeTable
      value={nodes}
      
      selectionKeys={selected}
      onSelectionChange={(e) => setSelected(e.value)}
      emptyMessage="No hay registros"
      propagateSelectionDown
      propagateSelectionUp
    >
      <Column header={headerName} body={firstColumnBody} expander />
      {columns.map((c, i) => (
        <Column
          key={i}
          header={c.header}
          body={makeBody(c)}
          style={{ width: c.width }}
          align={c.align}
        />
      ))}
      {(onDelete || onPrint) && (
        <Column body={accionesTemplate} header="" style={{ width: 110 }} />
      )}
    </TreeTable>
    </div>
  );
}
